"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useAuth } from "@clerk/nextjs";
import NumberFlow, { continuous } from "@number-flow/react";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { Sprout } from "lucide-react";
import { useMemo, useState } from "react";
import { PageEyebrow } from "@/components/public/page-eyebrow";
import { PUBLIC_CONTAINER, PUBLIC_SECTION_PY_CONTENT } from "@/components/public/public-layout-styles";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ButtonLink } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatAnnualSavings, formatRecurringIntervalSuffix, getPlanCopy, getProductPlanCode, isPerProjectPlan, planCardActionLabel, sortPricesByInterval, type PublicBillingUnit, yearlyAmountAsMonthly } from "@/lib/pricing-display";
import { buildPublicPricingFaq, catalogHasPerProjectPlan, shouldShowPerProjectFaq } from "@/lib/pricing-faq";
import { buildPricingActionHref, parsePlanIntent, persistPlanIntent } from "@/lib/pricing-intent";
import { cn } from "@/lib/utils";
type PublicPlan = FunctionReturnType<typeof api.payments.catalog.listPublicSubscriptionPlans>[number];
type PricingOffer = FunctionReturnType<typeof api.payments.catalog.getPublicPricingOffer>;
const PRICE_NUMBER_EASE_OUT = "cubic-bezier(0.215, 0.61, 0.355, 1)";
const PRICE_NUMBER_TIMING = {
  duration: 260,
  easing: PRICE_NUMBER_EASE_OUT
} satisfies EffectTiming;
const PRICE_NUMBER_OPACITY_TIMING = {
  duration: 120,
  easing: PRICE_NUMBER_EASE_OUT
} satisfies EffectTiming;
const PRICE_NUMBER_PLUGINS = [continuous];
function formatPrice(amount: number | undefined, currency: string) {
  if (amount === undefined) return "Sob consulta";
  // ui-audit-ignore-next-line format/use-shared-formatters: Stripe prices carry their own currency
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency.toUpperCase()
  }).format(amount / 100);
}
function intervalName(metadata: Record<string, string> | undefined, interval: string | undefined) {
  if (metadata?.billingInterval === "month" || interval === "month") return "Mensal";
  if (metadata?.billingInterval === "year" || interval === "year") return "Anual";
  return "Plano";
}
function PricingPlansSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="border-border divide-border grid divide-y border-y lg:grid-cols-3 lg:divide-x lg:divide-y-0">{Array.from({
        length: 3
      }, _temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, index) {
  return <div className="flex flex-col gap-6 py-8 lg:px-8" key={index}><Skeleton className="h-3 w-28" /><Skeleton className="h-8 w-36" /><Skeleton className="h-12 w-40" /><Skeleton className="h-4 w-full" /><Skeleton className="mt-auto h-10 w-full" /></div>;
}
function pricingLead(offer: PricingOffer) {
  const trial = offer.trialPeriodDays > 0 ? ` Planos pagos incluem ${offer.trialPeriodDays} dias de teste.` : "";
  if (offer.billingUnit === "per_user") {
    return `A cobrança considera os usuários ativos da organização.${trial} A assinatura é concluída no workspace.`;
  }
  if (offer.billingUnit === "per_project") {
    return `Escolha pelo número de projetos.${trial} A assinatura é concluída no workspace da organização.`;
  }
  return `Os valores vêm do catálogo de assinaturas.${trial} A assinatura é concluída no workspace da organização.`;
}
function PricingPlanColumn(t0) {
  const $ = _c(55);
  const {
    plan,
    isSignedIn,
    billingUnit
  } = t0;
  const {
    product,
    prices: rawPrices
  } = plan;
  let t1;
  if ($[0] !== rawPrices) {
    t1 = sortPricesByInterval(rawPrices);
    $[0] = rawPrices;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const prices = t1;
  const defaultInterval = prices[0]?.recurringInterval ?? "month";
  const [selectedInterval, setSelectedInterval] = useState(defaultInterval);
  const [animateBillingChange, setAnimateBillingChange] = useState(false);
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== animateBillingChange || $[3] !== billingUnit || $[4] !== defaultInterval || $[5] !== isSignedIn || $[6] !== prices || $[7] !== product.description || $[8] !== product.metadata || $[9] !== product.name || $[10] !== selectedInterval) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const selectedPrice = prices.find(price => (price.recurringInterval ?? defaultInterval) === selectedInterval) ?? prices[0];
      const featured = product.metadata?.featured === "true";
      const planCode = getProductPlanCode(product.metadata);
      const copy = getPlanCopy(planCode);
      const title = copy?.title ?? product.name;
      const audience = copy?.audience;
      const limit = copy?.limit;
      const detail = copy?.detail ?? product.description;
      let t16;
      if ($[26] !== prices) {
        const monthlyPrice = prices.find(_temp2);
        const yearlyPrice = prices.find(_temp3);
        t16 = monthlyPrice?.unitAmount !== undefined && yearlyPrice?.unitAmount !== undefined ? formatAnnualSavings(monthlyPrice.unitAmount, yearlyPrice.unitAmount) : null;
        $[26] = prices;
        $[27] = t16;
      } else {
        t16 = $[27];
      }
      const annualSavings = t16;
      if (!selectedPrice) {
        t15 = null;
        break bb0;
      }
      const isPerProject = isPerProjectPlan(selectedPrice.metadata, product.metadata);
      const intervalSuffix = formatRecurringIntervalSuffix({
        interval: selectedPrice.recurringInterval,
        count: selectedPrice.recurringIntervalCount,
        isPerProject,
        billingUnit
      });
      const planIntent = parsePlanIntent({
        plan: planCode,
        interval: selectedPrice.recurringInterval
      });
      const planActionHref = buildPricingActionHref({
        isSignedIn,
        intent: planIntent
      });
      const t17 = featured && "bg-surface";
      if ($[28] !== t17) {
        t9 = cn("flex flex-col py-8 lg:px-8", t17);
        $[28] = t17;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      let t18;
      if ($[30] !== featured) {
        t18 = featured ? <span className="text-brand-accent"> · Recomendado</span> : null;
        $[30] = featured;
        $[31] = t18;
      } else {
        t18 = $[31];
      }
      t10 = <p className="text-muted-foreground text-xs font-medium tracking-[0.14em] uppercase">{audience ?? "Plano"}{t18}</p>;
      t11 = <h2 className="mt-3 text-3xl font-semibold tracking-tight">{title}</h2>;
      t12 = limit ? <p className="text-muted-foreground mt-2 text-sm">{limit}</p> : null;
      const t19 = featured ? "text-5xl" : "text-4xl";
      let t20;
      if ($[32] !== t19) {
        t20 = cn("font-semibold tracking-tight tabular-nums", t19);
        $[32] = t19;
        $[33] = t20;
      } else {
        t20 = $[33];
      }
      const t21 = prices.map(price_3 => {
        const isSelected_0 = price_3._id === selectedPrice._id;
        const monthlyEquivalent = price_3.recurringInterval === "year" && price_3.unitAmount !== undefined ? yearlyAmountAsMonthly(price_3.unitAmount) : null;
        return <div aria-hidden={!isSelected_0} className={cn("col-start-1 row-start-1 motion-reduce:transition-none", animateBillingChange && cn("transition-[opacity,translate] ease-[cubic-bezier(0.32,0.72,0,1)]", isSelected_0 ? "duration-180" : "duration-130"), isSelected_0 ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-1 opacity-0")} key={price_3._id}>{monthlyEquivalent !== null ? <p className="text-muted-foreground mt-2 text-sm">Equivale a {formatPrice(monthlyEquivalent, price_3.currency)}/mês{annualSavings ? ` · ${annualSavings}` : null}</p> : null}{price_3.metadata?.installmentNote ? <p className="text-muted-foreground mt-1 text-xs">{price_3.metadata.installmentNote}</p> : null}</div>;
      });
      let t22;
      if ($[34] !== t21) {
        t22 = <div className="grid">{t21}</div>;
        $[34] = t21;
        $[35] = t22;
      } else {
        t22 = $[35];
      }
      t13 = <div className="mt-6"><p className={t20}>{prices.length <= 1 || selectedPrice.unitAmount === undefined ? formatPrice(selectedPrice.unitAmount, selectedPrice.currency) : <NumberFlow animated={animateBillingChange} className="inline-flex items-baseline leading-none tabular-nums" format={{
            style: "currency",
            currency: selectedPrice.currency.toUpperCase()
          }} locales="pt-BR" opacityTiming={PRICE_NUMBER_OPACITY_TIMING} plugins={PRICE_NUMBER_PLUGINS} respectMotionPreference={true} spinTiming={PRICE_NUMBER_TIMING} transformTiming={PRICE_NUMBER_TIMING} value={selectedPrice.unitAmount / 100} />}{intervalSuffix ? <span className="text-muted-foreground ml-1.5 text-base font-normal">{intervalSuffix}</span> : null}</p>{prices.length > 1 ? <fieldset aria-label={"Per\xEDodo de cobran\xE7a"} className="relative mt-3 -ml-2 inline-grid auto-cols-fr grid-flow-col text-sm font-medium"><span aria-hidden="true" className={cn("pointer-events-none absolute bottom-0 left-0 h-px motion-reduce:transition-none", animateBillingChange && "transition-transform duration-180 ease-[cubic-bezier(0.65,0,0.35,1)]")} style={{
            width: `${100 / prices.length}%`,
            transform: `translateX(${prices.indexOf(selectedPrice) * 100}%)`
          }}><span className="bg-foreground mx-2 block h-full" /></span>{prices.map((price_2, index) => {
            const interval = price_2.recurringInterval ?? defaultInterval;
            const isSelected = interval === selectedInterval;
            const label = intervalName(price_2.metadata, price_2.recurringInterval);
            return <button aria-pressed={isSelected} className={cn("focus-visible:outline-ring relative px-2 focus-visible:outline-2 focus-visible:outline-offset-4 motion-reduce:transition-none", animateBillingChange && "transition-colors duration-150 ease-out", isSelected ? "text-foreground" : "text-muted-foreground hover:text-foreground")} key={price_2._id} onClick={event => {
              if (isSelected) {
                return;
              }
              setAnimateBillingChange(event.detail > 0);
              setSelectedInterval(interval);
            }} type="button">{index > 0 ? <span aria-hidden="true" className="text-muted-foreground absolute left-0 -translate-x-1/2">·</span> : null}{label}</button>;
          })}</fieldset> : null}{t22}</div>;
      t14 = detail ? <p className="text-muted-foreground mt-6 max-w-[32ch] text-sm">{detail}</p> : null;
      t8 = "mt-auto pt-8";
      T0 = ButtonLink;
      t2 = "w-full";
      t3 = planActionHref;
      t4 = () => {
        if (planIntent) {
          persistPlanIntent(planIntent);
        }
      };
      t5 = "lg";
      t6 = featured ? "marketing" : "outline";
      t7 = planCardActionLabel(planCode, isSignedIn);
    }
    $[2] = animateBillingChange;
    $[3] = billingUnit;
    $[4] = defaultInterval;
    $[5] = isSignedIn;
    $[6] = prices;
    $[7] = product.description;
    $[8] = product.metadata;
    $[9] = product.name;
    $[10] = selectedInterval;
    $[11] = T0;
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
    $[15] = t13;
    $[16] = t14;
    $[17] = t15;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    T0 = $[11];
    t10 = $[12];
    t11 = $[13];
    t12 = $[14];
    t13 = $[15];
    t14 = $[16];
    t15 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[36] !== T0 || $[37] !== t2 || $[38] !== t3 || $[39] !== t4 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7) {
    t16 = <T0 className={t2} href={t3} onClick={t4} size={t5} variant={t6}>{t7}</T0>;
    $[36] = T0;
    $[37] = t2;
    $[38] = t3;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t16 || $[45] !== t8) {
    t17 = <div className={t8}>{t16}</div>;
    $[44] = t16;
    $[45] = t8;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t12 || $[50] !== t13 || $[51] !== t14 || $[52] !== t17 || $[53] !== t9) {
    t18 = <article className={t9}>{t10}{t11}{t12}{t13}{t14}{t17}</article>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t17;
    $[53] = t9;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  return t18;
}
function _temp3(price_1) {
  return price_1.recurringInterval === "year";
}
function _temp2(price_0) {
  return price_0.recurringInterval === "month";
}
export function PricingContent() {
  const $ = _c(20);
  const {
    isSignedIn
  } = useAuth();
  const plans = useQuery(api.payments.catalog.listPublicSubscriptionPlans);
  const offer = useQuery(api.payments.catalog.getPublicPricingOffer);
  const signedIn = isSignedIn === true;
  if (plans === undefined || offer === undefined) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = cn(PUBLIC_CONTAINER, PUBLIC_SECTION_PY_CONTENT, "space-y-8 lg:py-12");
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <main className="relative z-10 flex-1"><section className={t0}><div className="space-y-4"><Skeleton className="h-8 w-48 rounded-full" /><Skeleton className="h-12 w-full max-w-lg" /><Skeleton className="h-6 w-full max-w-xl" /></div><PricingPlansSkeleton /></section></main>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t0;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn(PUBLIC_CONTAINER, PUBLIC_SECTION_PY_CONTENT, "space-y-8 lg:py-12");
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PageEyebrow icon={Sprout}>Planos AgroProj</PageEyebrow>;
    t2 = <h1 className="text-foreground text-4xl font-semibold tracking-tight text-balance sm:text-5xl">Assinaturas para operações agrícolas</h1>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== offer) {
    t3 = pricingLead(offer);
    $[5] = offer;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <div className="max-w-2xl space-y-4">{t1}{t2}<p className="text-muted-foreground text-lg text-pretty">{t3}</p></div>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== offer || $[10] !== plans || $[11] !== signedIn) {
    t5 = plans.length === 0 ? <div className="border-border border-y py-10"><h2 className="text-lg font-semibold">Planos indisponíveis</h2><p className="text-muted-foreground mt-2 max-w-2xl text-sm">O catálogo de assinaturas ainda não foi sincronizado. Assim que os planos estiverem disponíveis, eles aparecerão nesta página.</p></div> : <div className="border-border divide-border grid divide-y border-y lg:grid-cols-3 lg:divide-x lg:divide-y-0">{plans.map(plan => <PricingPlanColumn billingUnit={offer.billingUnit} isSignedIn={signedIn} key={plan.product._id} plan={plan} />)}</div>;
    $[9] = offer;
    $[10] = plans;
    $[11] = signedIn;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== offer || $[14] !== plans) {
    t6 = plans.length > 0 ? <div className="max-w-3xl space-y-4"><h2 className="text-xl font-semibold tracking-tight">Perguntas frequentes</h2><Accordion className="rounded-2xl">{buildPublicPricingFaq({
          trialPeriodDays: offer.trialPeriodDays,
          trialRequiresCard: offer.trialRequiresCard,
          showPerProjectComparison: shouldShowPerProjectFaq({
            billingUnit: offer.billingUnit,
            catalogHasPerProject: catalogHasPerProjectPlan(plans)
          })
        }).map(_temp4)}</Accordion></div> : null;
    $[13] = offer;
    $[14] = plans;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t7 = <main className="relative z-10 flex-1"><section className={t0}>{t4}{t5}{t6}</section></main>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function _temp4(item) {
  return <AccordionItem key={item.question} value={item.question}><AccordionTrigger className="px-4 text-sm">{item.question}</AccordionTrigger><AccordionContent className="text-muted-foreground px-4 text-sm">{item.answer}</AccordionContent></AccordionItem>;
}